import { AdminNav } from '@/components/admin-nav'
import { BatchSection } from '@/components/batch-section'
import { BlockListSection } from '@/components/block-list-section'
import { CacheSection } from '@/components/cache-section'
import { ControlPanel } from '@/components/control-panel'
import { EmailServiceSection } from '@/components/email-service-section'
import { GameModelPoolSection } from '@/components/game-model-pool-section'
import { ModelCatalogSection } from '@/components/model-catalog-section'
import { PasswordProtection } from '@/components/password-protection'
import { QuerySection } from '@/components/query-section'
import { ReconcileSection } from '@/components/reconcile-section'
import { StaticsSection } from '@/components/statics-section'
import { ThemeProvider } from '@/components/theme-provider'
import { TokenCostSection } from '@/components/token-cost-section'
import { Button } from '@/components/ui/button'
import { WhitelistSection } from '@/components/whitelist-section'
import { useI18n } from '@/lib/i18n'
import { StimulateGamesBatchResponse, trpcClient } from '@/lib/trpc'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'

type GameSettingType = string

function App() {
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState('')
  const [isComplete, setIsComplete] = useState(false)
  const [gameCount, setGameCount] = useState(1)
  const [gameSetting, setGameSetting] = useState<GameSettingType>(
    'D10_ULTIMATE_TRIAL',
  )
  const [goodModels, setGoodModels] = useState<string[]>([])
  const [evilModels, setEvilModels] = useState<string[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState<string>('zh')
  const [showStimulationConfirm, setShowStimulationConfirm] = useState(false)
  const { t } = useI18n()

  const handleStimulateGame = async () => {
    setIsLoading(true)
    setResponse('')
    setIsComplete(false)

    try {
      // Convert single language to percentage format
      const singleLanguagePercentages = { [selectedLanguage]: 100 }

      const result: StimulateGamesBatchResponse
        = await trpcClient.batchRouter.stimulateGamesBatch.mutate({
          gameCount,
          languagePercentages: singleLanguagePercentages,
          gameSetting,
          goodModels,
          evilModels,
        })

      setResponse(
        `✅ ${t('admin.response.success')} ${result.message}\n\n`
        + `${t('admin.response.gameIds')}: ${result.gameIds.join(', ')}\n`
        + `${t('admin.response.totalGames')}: ${result.gameCount}\n`
        + `Model combinations: ${result.combinations}\n`
        + `Selected models: ${goodModels.join(', ')} (good) × ${evilModels.join(', ')} (evil)\n\n`
        + `Language Distribution:\n`
        + Object.entries(result.languageStats)
          .map(
            ([lang, stats]) =>
                `  ${lang}: ${stats.gamesCreated} games (${stats.gameIds.join(', ')})`,
          )
          .join('\n'),
      )
      setIsComplete(true)
    }
    catch (error) {
      console.error('Failed to stimulate game:', error)
      setResponse(
        `❌ ${t('admin.response.error')}: ${error instanceof Error ? error.message : t('admin.response.unknownError')}`,
      )
      setIsComplete(true)
    }
    finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    setResponse('')
    setIsComplete(false)
  }

  const handleShowStimulationConfirm = () => {
    setShowStimulationConfirm(true)
  }

  const handleConfirmStimulation = async () => {
    setShowStimulationConfirm(false)
    await handleStimulateGame()
  }

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <PasswordProtection
        password={import.meta.env.VITE_ADMIN_PASSWORD || 'admin123'}
      >
        <div className="min-h-screen bg-background text-foreground">
          <AdminNav />
          <main className="pt-16">

            <section id="model-catalog">
              <ModelCatalogSection />
            </section>
            <section id="game-model-pool">
              <GameModelPoolSection />
            </section>
            <section id="control-panel">
              <ControlPanel />
            </section>
            <section id="statics">
              <StaticsSection />
            </section>

            <section id="stimulation">
              <BatchSection
                response={response}
                isLoading={isLoading}
                isComplete={isComplete}
                onReset={handleReset}
                onStimulateGame={handleShowStimulationConfirm}
                gameCount={gameCount}
                onGameCountChange={setGameCount}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                gameSetting={gameSetting}
                onGameSettingChange={setGameSetting}
                goodModels={goodModels}
                onGoodModelsChange={setGoodModels}
                evilModels={evilModels}
                onEvilModelsChange={setEvilModels}
              />
            </section>

            <section id="email-service">
              <EmailServiceSection />
            </section>

            <section id="query">
              <QuerySection />
            </section>

            <section id="block-list">
              <BlockListSection />
            </section>

            <section id="whitelist">
              <WhitelistSection />
            </section>

            <section id="token-cost">
              <TokenCostSection />
            </section>

            <section id="reconcile">
              <ReconcileSection />
            </section>

            <section id="cache">
              <CacheSection />
            </section>
          </main>

          {/* Stimulation Confirmation Dialog */}
          {showStimulationConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="mx-4 w-full max-w-lg rounded-lg border bg-background p-6">
                <h3 className="mb-4 text-lg font-semibold">
                  {t('admin.stimulation.confirmTitle')}
                </h3>

                <div className="mb-6 space-y-4">
                  <div className="rounded-lg bg-muted/30 p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.gameCount')}:
                        </span>
                        <span className="ml-2 font-semibold">{gameCount}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.gameSetting')}:
                        </span>
                        <span className="ml-2 font-semibold">
                          {gameSetting}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.goodModel')}:
                        </span>
                        <span className="ml-2 font-semibold">{goodModels.join(', ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.evilModel')}:
                        </span>
                        <span className="ml-2 font-semibold">{evilModels.join(', ')}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">
                          {t('admin.config.language')}:
                        </span>
                        <span className="ml-2 font-semibold">
                          {selectedLanguage === 'zh' && '🇨🇳 简体中文'}
                          {selectedLanguage === 'en' && '🇺🇸 English'}
                          {selectedLanguage === 'fr' && '🇫🇷 Français'}
                          {selectedLanguage === 'ja' && '🇯🇵 日本語'}
                          {selectedLanguage === 'tw' && '🇭🇰 繁體中文'}
                          {selectedLanguage === 'es' && '🇪🇸 Español'}
                          {selectedLanguage === 'ar' && '🇸🇦 العربية'}
                          {selectedLanguage === 'ko' && '🇰🇷 한국어'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowStimulationConfirm(false)}
                    disabled={isLoading}
                  >
                    {t('admin.stimulation.cancel')}
                  </Button>
                  <Button
                    onClick={handleConfirmStimulation}
                    disabled={isLoading}
                    className="flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        {t('admin.loading')}
                      </>
                    ) : (
                      t('admin.stimulation.confirm')
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </PasswordProtection>
    </ThemeProvider>
  )
}

export default App
